import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Container, 
  Title, 
  Text, 
  Grid, 
  Stack, 
  Paper,
  Group,
  Badge,
  Alert,
  Loader,
  Center,
  Modal
} from '@mantine/core';
import { 
  IconShield, 
  IconUsers, 
  IconUpload, 
  IconAlertCircle,
  IconInfoCircle
} from '@tabler/icons-react';
import { PageContainer } from '../components/Container/PageContainer';
import { UserSearchPanel } from '../components/Admin/UserSearchPanel';
import { UserListGrid } from '../components/Admin/UserListGrid';
import { AvatarUploadForm } from '../components/Admin/AvatarUploadForm';
import { getAllUsers } from '../services/userAvatarService';

const AdminAvatarUpload = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadModal, setUploadModal] = useState(false);

  // 통계 계산
  const stats = useMemo(() => {
    const total = users.length;
    const withAvatar = users.filter(user => user.hasAvatar).length;
    const withoutAvatar = total - withAvatar;
    
    return { total, withAvatar, withoutAvatar };
  }, [users]);

  // 사용자 선택 핸들러
  const handleUserSelect = useCallback((user) => {
    setSelectedUser(user);
    setUploadModal(true);
  }, []);

  // 사용자 목록 변경 핸들러
  const handleUsersChange = useCallback((newUsers) => {
    setFilteredUsers(newUsers);
  }, []);

  // 필터 변경 핸들러
  const handleFilterChange = useCallback((filterInfo) => {
    console.log('Filter changed:', filterInfo);
  }, []);

  // 업로드 성공 핸들러
  const handleUploadSuccess = useCallback((result) => {
    console.log('Upload success:', result);
    
    // 사용자 목록 업데이트
    setUsers(prevUsers => 
      prevUsers.map(user => 
        user.id === selectedUser?.id 
          ? { ...user, hasAvatar: true }
          : user
      )
    );
    
    // 필터된 사용자 목록도 업데이트
    setFilteredUsers(prevUsers => 
      prevUsers.map(user => 
        user.id === selectedUser?.id 
          ? { ...user, hasAvatar: true }
          : user
      )
    );
    
    // 모달 닫기
    setUploadModal(false);
    setSelectedUser(null);
  }, [selectedUser]);

  // 초기 데이터 로드
  useEffect(() => {
    let isMounted = true;
    
    const loadInitialData = async () => {
      if (!isMounted) return;
      
      setLoading(true);
      try {
        console.log('🔄 컬렉션을 올린 사용자 데이터를 불러오는 중...');
        
        // 1. 모든 컬렉션을 불러오고
        const allUsers = await getAllUsers();
        
        if (!isMounted) return;
        
        console.log(`✅ ${allUsers.length}명의 사용자 데이터 로드 완료`);
        
        // 2. 여기서 유저리스트만 다시 추려 (이미 getAllUsers에서 필터링됨)
        setUsers(allUsers);
        setFilteredUsers(allUsers);
        
        // 개발 환경에서 관리자 설정 안내
        if (process.env.NODE_ENV === 'development' && allUsers.length === 0) {
          console.log('💡 개발 환경에서 관리자 설정이 필요할 수 있습니다:');
          console.log('1. 브라우저 콘솔에서 window.setupInitialAdmin("your-user-id") 실행');
          console.log('2. 또는 window.addDevAdmin("new-admin-id", "current-admin-id") 실행');
        }
      } catch (error) {
        if (!isMounted) return;
        
        console.error('❌ Initial data load failed:', error);
        
        // 구체적인 에러 메시지 표시
        if (error.code === 'permission-denied') {
          console.error('Firebase 권한 오류: 관리자 권한을 확인해주세요.');
        } else if (error.code === 'unavailable') {
          console.error('Firebase 연결 오류: 네트워크 연결을 확인해주세요.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          console.log('🎉 초기 데이터 로딩 완료');
        }
      }
    };

    loadInitialData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // 로딩 상태
  if (loading) {
    return (
      <PageContainer>
        <Center style={{ height: '50vh' }}>
          <Stack align="center" spacing="md">
            <Loader size="lg" />
            <Text>컬렉션을 올린 사용자 데이터를 불러오는 중...</Text>
            <Text size="sm" color="dimmed">
              잠시만 기다려주세요
            </Text>
          </Stack>
        </Center>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container size="xl" py="xl">
        <Stack spacing="xl">
          {/* 헤더 */}
          <Paper p="xl" withBorder>
            <Stack spacing="md">
              <Group position="apart" align="center">
                <Group>
                  <IconShield size={32} color="#228be6" />
                  <div>
                    <Title order={1}>관리자 아바타 업로드 시스템</Title>
                    <Text color="dimmed" size="sm">
                      컬렉션을 올린 사용자 아바타 이미지를 관리하고 업로드합니다
                    </Text>
                  </div>
                </Group>
                <Badge size="lg" variant="filled" color="blue">
                  관리자 모드
                </Badge>
              </Group>

              {/* 통계 */}
              <Group grow>
                <Paper p="md" withBorder>
                  <Group>
                    <IconUsers size={24} color="#228be6" />
                    <div>
                      <Text size="lg" weight={600}>{stats.total}</Text>
                      <Text size="sm" color="dimmed">전체 사용자</Text>
                    </div>
                  </Group>
                </Paper>
                
                <Paper p="md" withBorder>
                  <Group>
                    <IconUpload size={24} color="#40c057" />
                    <div>
                      <Text size="lg" weight={600} color="green">{stats.withAvatar}</Text>
                      <Text size="sm" color="dimmed">아바타 있음</Text>
                    </div>
                  </Group>
                </Paper>
                
                <Paper p="md" withBorder>
                  <Group>
                    <IconUpload size={24} color="#fa5252" />
                    <div>
                      <Text size="lg" weight={600} color="red">{stats.withoutAvatar}</Text>
                      <Text size="sm" color="dimmed">아바타 없음</Text>
                    </div>
                  </Group>
                </Paper>
              </Group>
            </Stack>
          </Paper>

          {/* 정보 알림 */}
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="사용 안내"
            color="blue"
            variant="light"
          >
            <Text size="sm">
              • 컬렉션을 올린 사용자만 표시됩니다.<br/>
              • 사용자를 검색하거나 필터링하여 아바타가 없는 사용자를 찾을 수 있습니다.<br/>
              • 사용자 카드를 클릭하여 상세 정보를 확인할 수 있습니다.<br/>
              • '업로드' 버튼을 클릭하여 아바타 이미지를 업로드할 수 있습니다.<br/>
              • 지원 형식: JPEG, PNG, WebP (최대 5MB)<br/>
              • 업로드된 이미지는 자동으로 리사이즈되어 최적화됩니다.
            </Text>
          </Alert>

          {/* 검색 패널 */}
          <UserSearchPanel
            users={users}
            onUsersChange={handleUsersChange}
            onFilterChange={handleFilterChange}
            loading={loading}
            setLoading={setLoading}
          />

          {/* 사용자 목록 */}
          <Paper p="md" withBorder>
            <Stack spacing="md">
              <Group position="apart">
                <Text size="lg" weight={600}>사용자 목록</Text>
                <Badge variant="light">
                  {filteredUsers.length}명 표시
                </Badge>
              </Group>
              
              {filteredUsers.length === 0 ? (
                <Center py="xl">
                  <Stack align="center" spacing="md">
                    <IconUsers size={48} color="gray" />
                    <Text color="dimmed">표시할 사용자가 없습니다</Text>
                    <Text size="sm" color="dimmed">
                      컬렉션을 올린 사용자가 없거나 검색 결과가 없습니다
                    </Text>
                  </Stack>
                </Center>
              ) : (
                <UserListGrid
                  users={filteredUsers}
                  onUserSelect={handleUserSelect}
                  selectedUser={selectedUser}
                  loading={false}
                />
              )}
            </Stack>
          </Paper>
        </Stack>
      </Container>

      {/* 업로드 모달 */}
      <Modal
        opened={uploadModal}
        onClose={() => {
          setUploadModal(false);
          setSelectedUser(null);
        }}
        title={`아바타 업로드 - ${selectedUser?.profile?.name || 'Unknown'}`}
        size="md"
      >
        {selectedUser && (
          <AvatarUploadForm
            selectedUser={selectedUser}
            onUploadSuccess={handleUploadSuccess}
          />
        )}
      </Modal>
    </PageContainer>
  );
};

export default AdminAvatarUpload; 